'use strict';

/*
  Acceptance Criteria:
  1) User must be able to select loan amounts in the increments of $100

  2) Maximum amount available for the user to select is the amount in the offer.

  3) Arrow buttons should not show for loan offer amounts where minValue == maxValue

  Default Message:
  The maximum amount you can borrow is [max_amount].

  Methods:

  getLoanAmount: returns the value in the hidden field
*/

function LoanAmountSelector() {
  this.id = null;
  this.node = null;
  this.extends = [];
  this.presenter = null;
  this.analytics = new Analytics();

  // Variable
  this._viewPath = '/member/components/loan-amount-selector';
  this.loanAmountValue = null;

  this.fieldErrorNode = null;
  // The model where the amount property is set on click, the value is then displayed in the UI.
  this.amountPropertyModel = null;
  this.error = null;

  this.onComplete = function () {};
  this.onClick = function () {};

  // min/max thousand values, max value depends on offer
  // passed in create
  this.minValue = this.minValue ? this.minValue : 0;
  this.maxValue = null;

  this._MAX_ERROR = 'The maximum amount you can borrow is ';
  this._MIN_ERROR = 'The minimum amount you can borrow is ';
  this._MAX_MIN_AMOUNTS = {
    thousands: {
      max: null,
      min: null
    }
  };

  // Component Classes
  this._classes = {
    class: 'mogo-component-loan-amount-selector',
    btns: 'mogo-component-loan-amount-selector__controls-btn',
    tabControl: 'mogo-component-loan-amount-selector__controls-tab',
    loanAmount: 'mogo-component-loan-amount-selector__loan-amount',
    value: 'mogo-component-loan-amount-selector__loan-amount-value',
    error: 'mogo-component-loan-amount-selector__error'
  };

  // KeyBoard Codes:
  this._keyBoardCodes = {
    arrowUp: 38,
    arrowDown: 40
  };

  this.init = function () {
    this._$node = $(this.node);

    this._$node.load(this._viewPath, this._initialize.bind(this));
  };

  this.initAllReady = function () {};

  this._initialize = function (data, response) {
    this._$node.html(data);

    // Perform these actions after html is injected in to the page
    this._calculateMinMaxAmounts();
    this._setupComputedVariable();
    this._setupAriaAttributes();
    this._setupDom();
    this._setupEventHandlers();
    this._displayLoanSelector();
  };

  this._calculateMinMaxAmounts = function () {
    this._MAX_MIN_AMOUNTS.thousands.min = Math.floor(this.minValue / 1000);
    this._MAX_MIN_AMOUNTS.thousands.max = Math.floor(this.maxValue / 1000);
  };

  /*
    _setupComputedVariable: add computed variables && populated aria values
  */
  this._setupComputedVariable = function () {
    var _this = this;

    this.presenter.addComputedVariable('loan_amount_selection', [this.amountPropertyModel], function (val) {
      _this.loanAmountValue = val;

      return val;
    });

    this.presenter.addComputedVariable('loan_amount_thousand', [this.amountPropertyModel], function (val) {
      var thousands = Math.floor(val / 1000);
      return thousands.toString() + ',';
    });

    this.presenter.addComputedVariable('loan_amount_hundred', [this.amountPropertyModel], function (val) {
      var hundreds = Math.floor(val % 1000);
      if (hundreds === 0) return '000';
      return hundreds.toString();
    });
  };

  /*
    _setupAriaAttributes: sets up aria-valuemin, aria-valuemax, aria-valuenow
    which tells the user agents the min value, max value and the value of Loan.
    Refer to beginning for default message;
  */
  this._setupAriaAttributes = function () {
    var val = this.presenter.getModelProperty(this.amountPropertyModel);

    this._updateAriaAttributes(val);
  };

  this._setupEventHandlers = function () {
    var _this2 = this;

    var btns = this._$node.find('.' + this._classes.btns);
    var tabControls = this._$node.find('.' + this._classes.tabControl);

    // Click:
    $.each(btns, function (item, element) {
      $(element).on('click', _this2._setupBtnEvents.bind(_this2));
    });

    // KeyDown Events:
    $.each(tabControls, function (item, element) {
      $(element).on('keydown', _this2._setupKeyboardEvents.bind(_this2));
    });
  };

  this._updateAriaAttributes = function (valueNow) {
    var _this3 = this;

    var btns = this._$node.find('.' + this._classes.btns);

    $.each(btns, function (item, element) {
      $(element).attr('aria-valuemin', _this3.minValue);
      $(element).attr('aria-valuemax', _this3.maxValue);
      $(element).attr('aria-valuenow', valueNow);
    });
  };

  this._setupKeyboardEvents = function (e) {
    // Arrow Up
    if (e.keyCode === this._keyBoardCodes.arrowUp) {
      this._increaseValue(e);
    }

    // Arrow Down
    if (e.keyCode === this._keyBoardCodes.arrowDown) {
      this._decreaseValue(e);
    }
  };

  this._setupBtnEvents = function (event) {
    var item = $(event.currentTarget);
    // data-direction: increase || decrease
    // constraint the range of next value
    // Increase logic
    if (item.data('direction') === 'increase') {
      this._increaseValue(event);
    }

    // Decrease logic
    if (item.data('direction') === 'decrease') {
      this._decreaseValue(event);
    }
  };

  this._decreaseValue = function (event) {
    event.preventDefault();
    var item = $(event.currentTarget);
    var incrementAmount = item.data('increment');
    var currentAmount = this.presenter.getModelProperty(this.amountPropertyModel);
    var currentThousands = Math.floor(currentAmount / 1000);
    var currentHundreds = currentAmount - currentThousands * 1000;

    if (incrementAmount === 1000) {
      currentThousands--;
      if (currentThousands < this._MAX_MIN_AMOUNTS.thousands.min) {
        currentThousands = this._MAX_MIN_AMOUNTS.thousands.max;
      }
    } else if (incrementAmount === 100) {
      currentHundreds = (currentHundreds - 100) % 1000;
      if (currentHundreds < 0) {
        currentHundreds = 900;
      }
    }

    var updatedAmount = currentThousands * 1000 + currentHundreds;

    if (updatedAmount > this.maxValue) {
      this._flashError('max');
    } else if (updatedAmount < this.minValue) {
      this._flashError('min');
    } else {
      this._fadeError();
    }

    this._updateValueProperties(updatedAmount);
  };

  this._increaseValue = function (event) {
    event.preventDefault();
    var item = $(event.currentTarget);
    var incrementAmount = item.data('increment');
    var currentAmount = this.presenter.getModelProperty(this.amountPropertyModel);
    var currentThousands = Math.floor(currentAmount / 1000);
    var currentHundreds = currentAmount - currentThousands * 1000;

    if (incrementAmount === 1000) {
      currentThousands++;
      if (currentThousands > this._MAX_MIN_AMOUNTS.thousands.max) {
        currentThousands = this._MAX_MIN_AMOUNTS.thousands.min;
      }
    } else if (incrementAmount === 100) {
      currentHundreds = (currentHundreds + 100) % 1000;
    }

    var updatedAmount = currentThousands * 1000 + currentHundreds;

    if (updatedAmount > this.maxValue) {
      this._flashError('max');
    } else if (updatedAmount < this.minValue) {
      this._flashError('min');
    } else {
      this._fadeError();
    }

    this._updateValueProperties(updatedAmount);
  };

  this._updateValueProperties = function (updatedAmount) {
    // Design has made this component confusing by utlizizing a loop mechanism,
    // they allow user to go above max value and show them an error message
    if (updatedAmount > this.maxValue || updatedAmount < this.minValue) {
      this.presenter.setModelProperty(this.amountPropertyModel, updatedAmount);
    } else {
      // update aria values
      this._updateAriaAttributes(updatedAmount);
    }

    // onUpdate value
    this.onClick(updatedAmount);
  };

  this._displayLoanSelector = function () {
    // If min and max amount are the same, there is only one option, therefore non editable
    if (this.minValue === this.maxValue) {
      // Parse FormatMoney thats in the non editable div
      $('#nonEditableLoanAmount').mogo().parse();
      $('.mogo-component-loan-amount-selector--non_editable').removeClass('hide');
    } else {
      $('.mogo-component-loan-amount-selector--editable').removeClass('hide');
    }
    this._$node.css('display', 'block');
  };

  /*
    _setupDomComplete: Sets up error label and callback functions.
  */
  this._setupDom = function () {
    this.fieldErrorNode = $('.' + this._classes.error);
    var labelArrow = '<span class="arrow"></span>';

    // Create FieldError
    $(this.fieldErrorNode).append(this._getDefaultErrorMessage() + labelArrow);

    // Component Callback for analytics.
    this.onComplete();
  };

  /* 
    Gets Error Message thats passed in config or the default message for LoanAmountSelector
    Used _input.js as reference:
  */
  this._getDefaultErrorMessage = function () {
    var defaultError = this._MAX_ERROR + this.maxValue + '.';

    return typeof this.error === 'string' ? this.error : defaultError;
  };

  this._flashError = function (type) {
    if (type === 'max') {
      $(this.fieldErrorNode)[0].firstChild.textContent = this._MAX_ERROR + Formatters.formatMoney(this.maxValue, { optionalDecimals: true }) + '.';
      this.analytics.forcePushEvent('loan-amount-customization', {
        LoanAmountCustomization: ['Loan Limit Exceeded']
      });
      $(this.fieldErrorNode).fadeIn(250);
    } else if (type === 'min') {
      $(this.fieldErrorNode)[0].firstChild.textContent = this._MIN_ERROR + Formatters.formatMoney(this.minValue, { optionalDecimals: true }) + '.';
      this.analytics.forcePushEvent('loan-amount-customization', {
        LoanAmountCustomization: ['Loan Limit Below Minimum']
      });
      $(this.fieldErrorNode).fadeIn(250);
    }
  };

  this._fadeError = function () {
    $(this.fieldErrorNode).fadeOut(250);
  };

  /*
    returns the loan amount value from the hidden field.
  */
  this.getLoanAmount = function () {
    return this.loanAmountValue;
  };

  this.destroy = function () {
    this.presenter.removeComputedVariable('loan_amount_selection');
    this.presenter.removeComputedVariable('loan_amount_thousand');
    this.presenter.removeComputedVariable('loan_amount_hundred');
  };
}